import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebase"; // Assumes functions is exported from firebase/firebase
import DynamicForm, { evalVisibleIf } from "../components/DynamicForm";

export default function SharedFormPage({ tokenOverride }) {
  const params = useParams();
  const token = tokenOverride || params.token;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkData, setLinkData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [success, setSuccess] = useState(false);

  // Form state
  const [values, setValues] = useState({});
  const [imageKeys, setImageKeys] = useState([]); // Not really used for public forms usually, but DynamicForm might need it

  useEffect(() => {
    if (!token) {
      setError("Lien invalide (token manquant).");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const ref = doc(db, "public_form_links", token);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setError("Ce lien est invalide ou a été supprimé.");
          setLoading(false);
          return;
        }
        const data = snap.data();
        
        // Basic client-side checks (backend also enforces them)
        if (data.status !== "active") {
          setError("Ce lien a été désactivé par son propriétaire.");
          setLoading(false);
          return;
        }
        if (data.expiresAt) {
          const now = new Date();
          const exp = data.expiresAt.toDate ? data.expiresAt.toDate() : new Date(data.expiresAt);
          if (now > exp) {
            setError("Ce lien a expiré.");
            setLoading(false);
            return;
          }
        }
        if (!data.configSnapshot) {
          setError("Configuration du formulaire introuvable.");
          setLoading(false);
          return;
        }

        setLinkData(data);
      } catch (err) {
        console.error("Error loading shared link:", err);
        setError("Erreur lors du chargement du formulaire.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Prepare fields for DynamicForm
  const fields = useMemo(() => {
    if (!linkData?.configSnapshot?.fields) return [];

    const rawFields = linkData.configSnapshot.fields;

    // ✅ IMPORTANT: garder les vraies keys quand fields est un objet
    let list = [];
    if (Array.isArray(rawFields)) {
      list = rawFields.filter(Boolean).map((f, idx) => ({
        ...f,
        key:
          f?.key ??
          f?.fieldKey ??
          f?.field_key ??
          f?.id ??
          f?.name ??
          `field_${idx}`,
      }));
    } else if (rawFields && typeof rawFields === "object") {
      // Correction: utiliser Object.entries pour ne pas perdre les clés
      list = Object.entries(rawFields)
        .filter(([, f]) => !!f)
        .map(([k, f], idx) => {
          // ✅ Priorité absolue à la clé de l'objet (k) car c'est elle qui est perdue par Object.values()
          // On trim() pour éviter les soucis d'espaces
          const key = k?.trim() || f?.key || f?.id || f?.name || `field_${idx}`;
          return { ...f, key };
        });
    }

    // Debug logs pour comprendre le mismatch si ça persiste
    // console.log("SharedFormPage fields keys:", list.map(f => f.key));
    
    // ✅ Sur la page partagée: pas d'avertissement "inDocx" => on force inDocx=true
    return list
      .map((f) => ({ ...f, inDocx: true }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [linkData]);

  const sections = useMemo(() => {
    const raw = linkData?.configSnapshot?.sections;
    const arr =
      Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.values(raw) : [];

    const norm = arr
      .filter(Boolean)
      .map((s, idx) => {
        const rawFields = s.fields ?? s.fieldKeys ?? s.keys ?? [];
        const fieldKeys = Array.isArray(rawFields)
          ? rawFields.map((x) => (typeof x === "string" ? x : x?.key ?? x?.id)).filter(Boolean)
          : [];

        return {
          id: s.id ?? s.key ?? `section_${idx}`,
          label: s.label ?? s.name ?? s.title ?? `Section ${idx + 1}`,
          order: s.order ?? idx,
          fields: fieldKeys,
        };
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return norm;
  }, [linkData]);

  const isEmptyValue = (v) =>
    v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

  const isRequired = (f) => f?.required === true || f?.required === "true" || f?.required === 1;

  const isVisible = (f, ctxValues) => {
    const cond = f?.visible_if ?? f?.visibleIf;
    if (!cond) return true;
    try {
      return !!evalVisibleIf(cond, ctxValues);
    } catch (e) {
      // En cas d'erreur de condition, on préfère afficher plutôt que bloquer
      return true;
    }
  };

  const getMissingRequired = (cfg, vals) => {
    const fields = Array.isArray(cfg?.fields) ? cfg.fields : [];
    const missing = [];

    for (const f of fields) {
      if (!isRequired(f)) continue;

      const groupKey = f.repeat_section || f.groupKey;
      if (groupKey) {
        const rows = Array.isArray(vals?.[groupKey]) ? vals[groupKey] : [];
        rows.forEach((row, idx) => {
          const ctx = { ...(vals || {}), ...(row || {}) };
          if (!isVisible(f, ctx)) return;
          const v = row?.[f.key];
          if (isEmptyValue(v)) {
            missing.push(`${f.label || f.key} (ligne ${idx + 1})`);
          }
        });
      } else {
        if (!isVisible(f, vals || {})) continue;
        const v = vals?.[f.key];
        if (isEmptyValue(v)) {
          missing.push(f.label || f.key);
        }
      }
    }

    return missing;
  };

  const handleSubmit = async () => {
    setSubmitErrors([]);
    const missing = getMissingRequired({ fields }, values);
    if (missing.length) {
      setSubmitErrors(missing);
      // notify(`Champs requis manquants : ${missing.join(", ")}`, "error");
      return;
    }

    if (!token) return;
    setSubmitting(true);
    try {
      const submitFn = httpsCallable(functions, "submitSharedForm");
      const payload = {
        token,
        values,
        meta: {
          userAgent: navigator.userAgent,
          source: "public_share",
          submittedAt: new Date().toISOString()
        }
      };
      
      const res = await submitFn(payload);
      if (res.data && res.data.ok) {
        setSuccess(true);
        setValues({}); // Reset form
      } else {
        throw new Error("Réponse inattendue du serveur.");
      }
    } catch (err) {
      console.error("Submission error:", err);
      alert("Erreur lors de l'envoi : " + (err.message || "Inconnue"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "#f5f7fa" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "#f5f7fa" }}>
        <div className="card shadow-sm p-5 text-center" style={{ maxWidth: 500 }}>
          <h2 className="text-danger mb-3"><i className="fas fa-exclamation-circle"></i></h2>
          <h3>Lien inaccessible</h3>
          <p className="text-muted">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "#f5f7fa" }}>
        <div className="card shadow-sm p-5 text-center" style={{ maxWidth: 500 }}>
          <h2 className="text-success mb-3"><i className="fas fa-check-circle"></i></h2>
          <h3>Envoyé !</h3>
          <p className="text-muted">Merci, vos réponses ont bien été enregistrées.</p>
          <button className="btn btn-outline-primary mt-3" onClick={() => setSuccess(false)}>
            Envoyer une autre réponse
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa", padding: "2rem 1rem" }}>
      <div className="container" style={{ maxWidth: 800 }}>
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-bottom-0 pt-4 pb-0 px-4">
            <h1 className="h3 mb-1">{linkData?.title || "Formulaire partagé"}</h1>
            <p className="text-muted small">
              Remplissez les informations ci-dessous.
            </p>
          </div>
          <div className="card-body px-4 pb-4">
            {submitErrors.length > 0 && (
              <div className="alert alert-danger">
                <div><strong>Champs requis manquants :</strong></div>
                <ul className="mb-0">
                  {submitErrors.map((name, i) => (
                    <li key={`${name}-${i}`}>{name}</li>
                  ))}
                </ul>
              </div>
            )}

            <DynamicForm
              sections={sections}
              fields={fields}
              values={values}
              setValues={setValues}
              onChange={(key, value) => {
                setValues((prev) => ({ ...prev, [key]: value }));
              }}
              readOnly={false}
              hidePreview
            />

            <div className="mt-4 d-flex justify-content-end">
              <button 
                className="btn btn-primary btn-lg px-5"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Envoi...
                  </>
                ) : (
                  <>
                    Envoyer <i className="fas fa-paper-plane ms-2"></i>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="text-center mt-3 text-muted small">
          Propulsé par Form2Doc
        </div>
      </div>
    </div>
  );
}
